import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('loading'); // loading | verified | already_verified | error
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) { setStatus('error'); setErrorMsg('No verification token provided.'); return; }
    const verify = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/verify-email/${token}`);
        setEmail(res.data.email || '');
        setName(res.data.name || '');
        setStatus(res.data.status === 'already_verified' ? 'already_verified' : 'verified');
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Verification failed.');
      }
    };
    verify();
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
        <div className="glass-card p-12 text-center max-w-md" data-testid="verify-loading">
          <Loader2 className="w-10 h-10 animate-spin text-[#E8720C] mx-auto mb-4" />
          <p className="text-white/50">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <Link to="/" className="inline-block">
              <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" />
            </Link>
          </div>
          <div className="glass-card p-8 text-center" data-testid="verify-error">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Verification Failed</h2>
            <p className="text-white/50 text-sm mb-6">{errorMsg}</p>
            <Link to="/register" className="text-[#E8720C] hover:underline text-sm" data-testid="verify-retry-link">
              Try registering again
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Verified or already_verified → redirect to set password
  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" />
          </Link>
        </div>
        <div className="glass-card p-8 text-center" data-testid="verify-success">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Email Verified!</h2>
          <p className="text-white/50 text-sm mb-6">
            {name ? `Welcome, ${name}!` : 'Welcome!'} Your email <strong className="text-white/80">{email}</strong> has been verified.
            Now set your password to complete registration.
          </p>
          <Link
            to={`/set-password?token=${encodeURIComponent(token)}`}
            className="btn-gold py-3 px-8 inline-block"
            data-testid="verify-set-password-btn"
          >
            Set Password & Complete
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
